import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Field, Empty, NeedClinic } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, useLoad } from "@/lib/api";
import { can } from "@/lib/constants";

const blank = { first_name: "", last_name: "", gender: "", dob: "", phone: "", email: "", address: "", insurance_provider: "", insurance_number: "" };

function PatientForm({ f, setF, insurance }) {
  const inp = (k, label, type = "text") => (
    <Field label={label}><Input type={type} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`patient-${k.replace("_", "-")}-input`} /></Field>);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {inp("first_name", "First name")}{inp("last_name", "Last name")}
      <Field label="Gender"><Select value={f.gender || undefined} onValueChange={(v) => setF({ ...f, gender: v })}>
        <SelectTrigger data-testid="patient-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></Field>
      {inp("dob", "Date of birth", "date")}{inp("phone", "Phone")}{inp("email", "Email", "email")}
      <div className="sm:col-span-2">{inp("address", "Address")}</div>
      {insurance && <>{inp("insurance_provider", "Insurance provider")}{inp("insurance_number", "Member / policy no.")}</>}
    </div>
  );
}

export default function Patients() {
  const { user, activeClinic, activeClinicId } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, reload] = useLoad(activeClinicId ? "/patients" : null, { clinic_id: activeClinicId, q });
  const [f, setF] = useState(null);
  if (!activeClinic) return <NeedClinic isCeo={user.role === "ceo"} />;
  const save = async () => {
    try {
      const { data } = await api.post("/patients", f, { params: { clinic_id: activeClinicId } });
      toast.success(`Patient ${data.patient_no} registered`);
      setF(null);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={activeClinic.name} title="Patients" subtitle="Outpatient register.">
        {can(user, "createPatient") && <Button className="rounded-full" onClick={() => setF(blank)} data-testid="add-patient-button"><Plus className="mr-2 h-4 w-4" />Register patient</Button>}
      </PageHeader>
      <Panel testId="patients-table">
        <div className="relative mb-4 max-w-sm"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9 rounded-full" placeholder="Search name, phone or PT number" value={q} onChange={(e) => setQ(e.target.value)} data-testid="patient-search-input" /></div>
        {!rows?.length ? <Empty text="No patients found." /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>No.</TableHead><TableHead>Name</TableHead><TableHead>Gender</TableHead><TableHead>Phone</TableHead><TableHead>Insurance</TableHead><TableHead>Registered</TableHead></TableRow></TableHeader>
            <TableBody>{rows.map((p) => (
              <TableRow key={p.id} className="cursor-pointer hover:bg-teal-50/40" onClick={() => nav(`/patients/${p.id}`)} data-testid={`patient-row-${p.id}`}>
                <TableCell className="font-mono text-xs text-emerald-700">{p.patient_no}</TableCell>
                <TableCell className="font-semibold">{p.first_name} {p.last_name}</TableCell>
                <TableCell className="capitalize">{p.gender || "—"}</TableCell>
                <TableCell>{p.phone || "—"}</TableCell>
                <TableCell>{p.insurance_provider || "—"}</TableCell>
                <TableCell className="text-sm text-slate-500">{fmtDate(p.created_at)}</TableCell>
              </TableRow>))}</TableBody>
          </Table></div>)}
      </Panel>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Register patient</DialogTitle></DialogHeader>
          {f && <PatientForm f={f} setF={setF} insurance={activeClinic.modules?.insurance} />}
          <DialogFooter><Button onClick={save} data-testid="patient-form-submit-button">Register</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
